import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Box,
  CardContent,
  CardActions,
  Chip,
  InputAdornment,
  Grid,
  Stack,
  alpha,
  useTheme as useMuiTheme,
  Tabs,
  Tab,
  Pagination,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useRoom } from "../contexts/RoomContext";
import { Room, RoomStatus, RoomType } from "../types";
import Loading from "../components/common/Loading";
import ErrorMessage from "../components/common/ErrorMessage";
import { useAuth } from "../contexts/AuthContext";
import StyledTextField from "../components/common/StyledTextField";
import StyledCard from "../components/common/StyledCard";
import StyledButton from "../components/common/StyledButton";

/**
 * Explore Component - Browse and join rooms
 */
const Explore = () => {
  const { publicRooms, loading, error, fetchAllRooms, joinRoom } = useRoom();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRooms, setFilteredRooms] = useState<Room[]>([]);
  const [roomType, setRoomType] = useState(0); // 0: Public, 1: Private
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useMuiTheme();

  // Fetch all rooms including private ones when component mounts
  useEffect(() => {
    fetchAllRooms();
  }, [fetchAllRooms]); // fetchAllRooms is memoized with useCallback

  // Check if the user is already part of the room
  const isUserInRoom = useCallback(
    (room: Room) => {
      if (!user) return false;

      // Check if user is the creator
      if (room.creator && typeof room.creator === "object") {
        // Handle the case where creator is an object that might have _id or id
        const creatorId = (room.creator as any)._id || room.creator.id;
        if (creatorId === user.id) return true;
      }

      // Check if user is a participant
      return room.participants.some(
        (participant) =>
          typeof participant === "object" && participant.id === user.id
      );
    },
    [user]
  );

  // Filter rooms when publicRooms or searchTerm changes
  useEffect(() => {
    if (publicRooms) {
      // First filter by public/private
      const roomsByType = publicRooms.filter((room) => {
        // For public rooms tab
        if (roomType === 0) return room.roomType === RoomType.PUBLIC;

        // For private rooms tab - include if it's private AND either:
        // 1. You are the creator
        // 2. You are invited/participating
        if (roomType === 1) {
          const isPrivate = room.roomType === RoomType.PRIVATE;

          // Check if you're the creator
          const isCreator =
            user &&
            room.creator &&
            typeof room.creator === "object" &&
            (room.creator as any)._id === user.id;

          // Check if you're a participant or invited
          const isParticipant = isUserInRoom(room);

          return isPrivate && (isCreator || isParticipant);
        }

        return false;
      });

      // Then filter by search term
      const filtered = roomsByType.filter((room) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          room.title.toLowerCase().includes(searchLower) ||
          room.description.toLowerCase().includes(searchLower) ||
          room.tags.some((tag) => tag.toLowerCase().includes(searchLower))
        );
      });

      setFilteredRooms(filtered);
      setPage(1); // Reset to first page whenever filters change
    }
  }, [publicRooms, searchTerm, roomType, user, isUserInRoom]);

  const formatDateTime = (dateString: string | Date) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // Memoize the join handler
  const handleJoin = useCallback(
    async (room: Room) => {
      try {
        // For closed rooms, just navigate to view the room details
        if (room.status === RoomStatus.CLOSED) {
          navigate(`/rooms/${room._id}`);
          return;
        }

        // If user is already in the room, just navigate to it
        if (isUserInRoom(room)) {
          navigate(`/rooms/${room._id}`);
        } else {
          // Otherwise, join the room first
          await joinRoom(room._id);
          navigate(`/rooms/${room._id}`);
        }
      } catch (error: any) {
        console.error("Error joining room:", error);
        // If we get an "Already joined" error, just navigate to the room
        if (error.response?.data?.message === "Already joined this room") {
          navigate(`/rooms/${room._id}`);
        }
      }
    },
    [joinRoom, navigate, isUserInRoom]
  );

  const handleRoomTypeChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setRoomType(newValue);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  // Calculate pagination
  const paginatedRooms = filteredRooms.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );
  const totalPages = Math.ceil(filteredRooms.length / itemsPerPage);

  if (loading) return <Loading message="Loading rooms..." />;

  return (
    <Box
      sx={{
        mb: 4,
        minHeight: "100vh",
        bgcolor: "background.default",
        backgroundImage:
          theme.palette.mode === "light"
            ? `radial-gradient(circle at 20% 80%, ${alpha(
              theme.palette.primary.main,
              0.03
            )} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${alpha(
              theme.palette.secondary.main,
              0.03
            )} 0%, transparent 50%)`
            : `radial-gradient(circle at 20% 80%, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${alpha(
              theme.palette.secondary.main,
              0.05
            )} 0%, transparent 50%)`,
        py: 4,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          mb: 4,
          animation: "slideInTop 0.6s ease-out",
          "@keyframes slideInTop": {
            from: {
              opacity: 0,
              transform: "translateY(-20px)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
        id="explore-header"
      >
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 800,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textAlign: "center",
            mb: 1.5,
            fontSize: { xs: "2rem", md: "3rem" },
            letterSpacing: "-0.02em",
          }}
        >
          Explore Rooms
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            textAlign: "center",
            fontSize: { xs: "1rem", md: "1.1rem" },
            maxWidth: "600px",
            mx: "auto",
            lineHeight: 1.6,
          }}
        >
          Discover public rooms, join communities, or connect with friends through private rooms.
        </Typography>
      </Box>

      <ErrorMessage message={error} />

      {/* Room Type Tabs */}
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          mb: 4,
          mt: 4,
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: alpha(theme.palette.divider, 0.1),
            background: alpha(theme.palette.background.paper, 0.5),
            borderRadius: 4,
            p: 1,
            backdropFilter: "blur(10px)",
            maxWidth: "600px",
            mx: "auto",
          }}
          id="explore-tabs"
        >
          <Tabs
            value={roomType}
            onChange={handleRoomTypeChange}
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontWeight: 600,
                transition: "all 0.3s ease",
                borderRadius: 3,
                mx: 0.5,
                minHeight: 48,
                "&.Mui-selected": {
                  color: theme.palette.primary.main,
                  background: alpha(theme.palette.primary.main, 0.1),
                },
                "&:hover": {
                  background: alpha(theme.palette.primary.main, 0.05),
                },
              },
              "& .MuiTabs-indicator": {
                height: 0,
              },
            }}
          >
            <Tab label="Public Rooms" />
            <Tab label="Private Rooms" />
          </Tabs>
        </Box>
      </Box>

      {/* Search Bar */}
      <Box
        sx={{
          mb: 4,
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: "800px",
          mx: "auto",
        }}
        id="explore-search"
      >
        <StyledTextField
          fullWidth
          variant="outlined"
          placeholder="Search by title, description, or tag..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.primary.main }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Results count */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        px={{ xs: 2, sm: 3, md: 4 }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          Showing {filteredRooms.length}{" "}
          {filteredRooms.length === 1 ? "room" : "rooms"}
        </Typography>

        {totalPages > 1 && (
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Page {page} of {totalPages}
          </Typography>
        )}
      </Box>

      {/* Rooms List */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, px: { xs: 2, sm: 3, md: 4 } }} id="explore-room-list">
        {paginatedRooms.length > 0 ? (
          paginatedRooms.map((room) => (
            <RoomCard
              key={room._id}
              room={room}
              onJoin={handleJoin}
              formatDate={formatDateTime}
              isUserInRoom={isUserInRoom(room)}
            />
          ))
        ) : (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No {roomType === 0 ? "public" : "private"} rooms found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or check back later for new events.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          display="flex"
          justifyContent="center"
          mt={6}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            size="large"
          />
        </Box>
      )}
    </Box>
  );
};

/**
 * Room Card Component for Explore page
 */
const RoomCard = ({
  room,
  onJoin,
  formatDate,
  isUserInRoom,
}: {
  room: Room;
  onJoin: (room: Room) => void;
  formatDate: (date: string | Date) => string;
  isUserInRoom: boolean;
}) => {
  const theme = useMuiTheme();

  const statusColors = {
    [RoomStatus.SCHEDULED]: {
      bg: theme.palette.info.main,
      text: theme.palette.info.contrastText,
    },
    [RoomStatus.LIVE]: {
      bg: theme.palette.success.main,
      text: theme.palette.success.contrastText,
    },
    [RoomStatus.CLOSED]: {
      bg:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.grey[500], 0.2)
          : alpha(theme.palette.grey[500], 0.1),
      text: theme.palette.text.secondary,
    },
  };

  const statusColor =
    statusColors[room.status] || statusColors[RoomStatus.CLOSED];

  const creatorName =
    room.creator && typeof room.creator === "object" && room.creator.username
      ? room.creator.username
      : "Unknown";

  // Determine button text based on user status and room status
  const getButtonText = () => {
    if (isUserInRoom) {
      return room.status === RoomStatus.LIVE ? "Enter Room" : "View Room";
    }
    return room.status === RoomStatus.LIVE ? "Join Now" : "View Details";
  };

  return (
    <StyledCard
      sx={{
        width: "100%",
        position: "relative",
        overflow: "visible",
        "&::before":
          room.status === RoomStatus.LIVE
            ? {
              content: '""',
              position: "absolute",
              top: -4,
              right: -4,
              width: 12,
              height: 12,
              backgroundColor: "success.main",
              borderRadius: "50%",
              boxShadow: "0 0 0 3px rgba(76, 175, 80, 0.3)",
              animation: "pulse 1.5s infinite",
              "@keyframes pulse": {
                "0%": {
                  boxShadow: "0 0 0 0 rgba(76, 175, 80, 0.6)",
                },
                "70%": {
                  boxShadow: "0 0 0 6px rgba(76, 175, 80, 0)",
                },
                "100%": {
                  boxShadow: "0 0 0 0 rgba(76, 175, 80, 0)",
                },
              },
            }
            : {},
      }}
    >
      {/* Card header with room title and status */}
      <CardContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTopLeftRadius: "inherit",
            borderTopRightRadius: "inherit",
            p: 2.5,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", maxWidth: "70%" }}>
            <Typography variant="h6" component="h2" fontWeight="700" noWrap>
              {room.title}
            </Typography>
            {room.roomType === RoomType.PRIVATE && (
              <Chip
                label="PRIVATE"
                size="small"
                sx={{
                  ml: 1.5,
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  height: 22,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                }}
              />
            )}
          </Box>
          <Chip
            label={room.status.toUpperCase()}
            size="small"
            sx={{
              background: `linear-gradient(135deg, ${statusColor.bg
                } 0%, ${alpha(statusColor.bg, 0.8)} 100%)`,
              color: statusColor.text,
              fontWeight: 700,
              fontSize: "0.7rem",
              borderRadius: 2,
              boxShadow: `0 2px 8px ${alpha(statusColor.bg, 0.3)}`,
            }}
          />
        </Box>

        {/* Card content with room details */}
        <Box sx={{ p: 2.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Created by: <span style={{ color: theme.palette.primary.main }}>{creatorName}</span>
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.secondary, bgcolor: alpha(theme.palette.background.default, 0.5), px: 1, py: 0.5, borderRadius: 1 }}>
              Max participants: {room.maxParticipants}
            </Typography>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{
              minHeight: "40px",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              mb: 2,
              lineHeight: 1.6,
            }}
          >
            {room.description}
          </Typography>

          <Stack spacing={1.5} sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>Start</span>
              <span style={{ color: theme.palette.text.secondary }}>{formatDate(room.startTime)}</span>
            </Typography>
            <Typography
              variant="body2"
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>End</span>
              <span style={{ color: theme.palette.text.secondary }}>{formatDate(room.endTime)}</span>
            </Typography>
          </Stack>

          {/* Room tags */}
          {room.tags.length > 0 && (
            <Box mt={2.5} display="flex" flexWrap="wrap" gap={0.5}>
              {room.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    color: theme.palette.secondary.main,
                    fontSize: "0.7rem",
                    height: 24,
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </CardContent>

      {/* Card actions */}
      <CardActions sx={{ p: 2.5, pt: 0 }}>
        <StyledButton
          size="large"
          color="primary"
          variant={
            room.status === RoomStatus.LIVE || isUserInRoom
              ? "contained"
              : "outlined"
          }
          onClick={() => onJoin(room)}
          fullWidth
          className="explore-join-btn"
          sx={{
            py: 1.2,
            borderRadius: 3,
            fontWeight: 600,
            fontSize: "0.95rem",
          }}
        >
          {room.status === RoomStatus.LIVE && "🔴 "}
          {room.status === RoomStatus.SCHEDULED && "⏰ "}
          {room.status === RoomStatus.CLOSED && "📋 "}
          {getButtonText()}
        </StyledButton>
      </CardActions>
    </StyledCard>
  );
};

export default Explore;
